<script setup>
    import { useStore } from 'vuex'
    import { useRoute, useRouter } from 'vue-router'
    import { defineAsyncComponent, computed, ref, inject, reactive, onMounted, getCurrentInstance } from 'vue';
    import { getParentPaymentsMethods, 
        onUploadImage, 
        submitDepositAmount, 
        ocrCheck,
        getPaymentGatewayUrl,
        createSharedComputedForDepositPayment
    } from '@template/utils';

     const { $errorToast, $helper, $t, $siteSettings, $successToast  } = getCurrentInstance().proxy;
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const BankAccountDetails = defineAsyncComponent(() => import('../components/BankAccountDetails.vue'));
    const UpiAccountDetails = defineAsyncComponent(() => import('../components/UpiAccountDetails.vue'));
    const WalletInstructionVideos = defineAsyncComponent(() => import('../components/WalletInstructionVideos.vue'));

    const walletConfig = inject('walletConfig')
    const depositAmount = computed(() => store.getters.depositAmount);
    const bonusData = computed(() => store.getters.bonusData);
    const hasFirstDepositBonus = computed(() => {
        return bonusData.value.find(bd => bd.bonusType == 'first deposit')
    });

    const hasEveryDepositBonus = computed(() => {
        return bonusData.value.find(bd => bd.bonusType == 'every deposit')
    });

    const methodIcons = computed(() => {
        const iconObj = {}

        walletConfig.value?.payment_method?.forEach(element => {
            iconObj[element.method_name] = element.icon
        })

        walletConfig.value?.bank_list?.forEach(element => {
            iconObj[element.bank_name] = element.icon
        })

        return iconObj
    });


    const parentPaymentsList = ref(null);
    const activeTab = ref(null);
    const currentPaymentId = ref(null);
    const paymentDetailsList = ref(null);
    const uploadedImage = ref(null);

    const manual_utr = ref('');
    const tesseract_utr = ref(null);
    const previewImage = ref(null);
    const accountDetails = ref(null);

    const termCondition = ref(false);
    const walletStake = ref(null);

    const init = reactive({
    utr: false,
    transaction_image_url: false,
    });

    const t = ref(6);                 
    const interval = ref(30);         
    const angle = ref(0);             
    const angle_increment = ref(360 / 9);
    const intervalCounter = ref(0);
    const timerValue = ref(9);        
    const timer = ref(null);          

    const utrLoader = ref(false);
    const has_edited_utr = ref(0);
    const isPaymentGateway = ref(null);
    const paymentProofFileUploadStatus = ref('');
    const utrSection = ref('');
    const sbtBtn = ref('');
    const utrMsg = ref('');
    const fileRef = ref(undefined);

    let paymentBonusRef = ref(null)

     onMounted(()=>{
        getParentPaymentsMethodsFunc()
    })

    const scrollToref = (element) => {
        setTimeout(() => {
            element.scrollIntoView({ 
                behavior: "smooth", 
                inline: "nearest",
                block: "end"
            });
        }, 200) 
    }

    const toggleInit = (bool) =>{
        init.utr = bool
        init.transaction_image_url = bool
    }

    const getParentPaymentsMethodsFunc = async(reconnectTry = 0) => {
        try {
            let response = await getParentPaymentsMethods( reconnectTry, depositAmount.value );
            if(response?.data){
                parentPaymentsList.value = response.data;
                let nonSocialStartIndex = response?.nonSocialStartIndex
                activeTab.value = Object.keys(parentPaymentsList.value)[nonSocialStartIndex]
                changePaymentMethod(parentPaymentsList.value[activeTab.value], activeTab.value);
                const { paymentBonus } = createSharedComputedForDepositPayment({bonusData: bonusData.value})
                paymentBonusRef = computed(paymentBonus);
            }
        } catch (error) {
            $errorToast(error)
        }
    }

    const changePaymentMethod = (payment, index) => {
        toggleInit(false)
        removeImage();
        activeTab.value = index;
        currentPaymentId.value = payment?.[0]?.method
        paymentDetailsList.value = payment
        accountDetails.value = paymentDetailsList.value?.[0]
        isPaymentGateway.value = false;
        manual_utr.value = '';
        tesseract_utr.value = null;
        if(payment?.[0]?.method == 'social'){
            const socialNumber = payment?.[0]?.social_payment_number;
            const socialLink = payment?.[0]?.social_payment_link;
            if (socialNumber?.length && socialLink?.length) {
                const choice = Math.random() < 0.5 ? 'number' : 'link';
                if (choice === 'number') {
                    if (payment?.[0]?.method_name?.toLowerCase().includes('whatsapp')) {
                        window.open(`https://wa.me/?text=${socialNumber}`, 'blank');
                    } else if (payment?.[0]?.method_name?.toLowerCase().includes('telegram')) {
                        window.open(`https://t.me/${socialNumber}`, 'blank');
                    }
                } else {
                    window.open(socialLink, 'blank');
                }
                return;
            }
            if (socialNumber?.length) {
                if (payment?.[0]?.method_name?.toLowerCase().includes('whatsapp')) {
                    window.open(`https://wa.me/?text=${socialNumber}`, 'blank');
                } else if (payment?.[0]?.method_name?.toLowerCase().includes('telegram')) {
                    window.open(`https://t.me/${socialNumber}`, 'blank');
                }
                return;
            }
            if (socialLink?.length) {
                window.open(socialLink, 'blank');
                return;
            }
            $errorToast($t('no-social-payment-number', 'No social payment number available'));
            return;
        }
        if(payment?.[0]?.method == 'payment_gateway'){
            isPaymentGateway.value = true;
            paymentDetailsList.value = payment;
            accountDetails.value = paymentDetailsList.value?.[0]
            return
        }
    }

    const submitDepositAmounts = async () => {
        toggleInit(true);

        if (
            (accountDetails.value?.method !== "wallet" && manual_utr.value.length < 10) ||
            !uploadedImage.value
        ) {
            return;
        }

        try {
            termCondition.value = true;
            const response = await submitDepositAmount({
            accountDetails: accountDetails.value,
            uploadedImage: uploadedImage.value,
            depositAmount: depositAmount.value,
            manual_utr: manual_utr.value,
            removeImage: removeImage.value,
            has_edited_utr: has_edited_utr.value,
            });

        } catch (error) {
            $errorToast(error);  
            termCondition.value = false;
        }
    };

    const onUploadImageFunc = async (event) => {
        try {
            paymentProofFileUploadStatus.value = 'Fetching payment proof file';
            const file = event.target.files[0];
             if (!file) {
                return $errorToast($t('no-file-selected','No File selected to upload'))
            }
            paymentProofFileUploadStatus.value = 'Payment Proof File found Ready to Process';
            previewImage.value = URL.createObjectURL(file);
            scrollToref(utrSection.value); 

            const response = await onUploadImage({
                event,
                depositAmount: depositAmount.value,
                startTimer,
                resetTimer,
                sbtBtn: sbtBtn.value,      // this.$refs.sbtBtn → sbtBtn ref
                utrLoader: utrLoader.value,
                paymentProofFileUploadStatus
            });

            if (response) {
                if(!response?.manual_utr){
                    paymentProofFileUploadStatus.value = 'UTR Number Not Found from above payment proof try with different one';
                }else{
                    paymentProofFileUploadStatus.value = 'UTR Number Has been successfully extracted !!!';
                }
                manual_utr.value = response.manual_utr;
                utrMsg.value = response.utrMsg;
                uploadedImage.value = response.uploadedImage;
                previewImage.value = response.previewImage;
                tesseract_utr.value = response.tesseract_utr;
                utrLoader.value = response.utrLoader;
            } else {
                paymentProofFileUploadStatus.value = 'Unable to Extract the UTR Number From Above Uploaded File , Please try with another file or try again after sometime';
                previewImage.value = null;
            }

        } catch (error) {
            previewImage.value = null;
            paymentProofFileUploadStatus.value = 'Unable to Extract the UTR Number From Above Uploaded File , Please try with another file or try again after sometime';
            $errorToast(error);
        }
        finally{
            setTimeout(() => {
                paymentProofFileUploadStatus.value = '';
            }, 2000);
        }
    };

    const startTimer = () => {
        utrLoader.value = true;

        $helper.scrollToElement(utrSection.value);

        angle.value = 0;
        intervalCounter.value = 0;
        timerValue.value = t.value;

        if (timer.value) {
            clearInterval(timer.value);
        }

        timer.value = setInterval(() => {
            intervalCounter.value++;

            angle.value =
            (intervalCounter.value / (t.value * (1000 / interval.value))) * 360;

            timerValue.value =
            t.value - Math.floor((angle.value / 360) * t.value);

            if (timerValue.value <= 0) {
            resetTimer();
            utrLoader.value = false;
            }
        }, interval.value);
    };

    const resetTimer = () => {
        clearInterval(timer.value);
        utrLoader.value = false;
        angle.value = 0;
        intervalCounter.value = 0;
        timerValue.value = t.value;
    }

    const changeAccount = (data) => {
        accountDetails.value  = data
        manual_utr.value  = '';
        previewImage.value = null;
        toggleInit(false)
    }
    const removeImage = () => {
        uploadedImage.value = null
        previewImage.value = null
        termCondition.value = false;
        has_edited_utr.value = 0
        manual_utr.value =''
    }

    const copyText = (content, label) => {
        let dummy = document.createElement('input'),
            text = content;
        document.body.appendChild(dummy);
        dummy.value = text;
        dummy.select();
        document.execCommand('copy');
        document.body.removeChild(dummy);
        $successToast(`${label} copied successfully !!`);
    }

    const sanitizeInput = () => {
        manual_utr.value = manual_utr.value.replace(/\D+/g, '');
    }

    const goToPaymentGatewayFunc = async()=> {
        try {
            store.dispatch('setGlobalLoader', true);
            const response = await getPaymentGatewayUrl(depositAmount.value, paymentDetailsList.value[0]);store.dispatch('setGlobalLoader', false);
        } catch (error) {
            store.dispatch('setGlobalLoader', false);
            $errorToast(error)
        }
    }
    
    const gotoWhatsappDeposit = () => {
        window.open('https://wa.me/' + $siteSettings?.data?.social_link?.whatsappno1 || $siteSettings?.data?.social_link?.whatsappno2, '_blank')
    }

    const onFilePickerOpen = () => { 
        window.addEventListener('focus', () => {        
            setTimeout(() => {
                const files = fileRef.value?.files;
                if (files?.length == 0 && !paymentProofFileUploadStatus.value) {
                    $errorToast('File not selected. Check permission or try again!!')
                }
            }, 1000);
        }, { once: true })
    }



</script>

<template>
    <div class="deposit-and-withdrawal-sec">
        <div class="container">
            <div class="choose-odds-sec" v-if="parentPaymentsList">
                <div class="lotbet-new-every-bonus-wrapper mt-2" v-if="hasFirstDepositBonus">
                    <div class="lotbet-bonus-card clickbet-first-login-bonus">
                        <div class="lotbet-star-decoration top-left">
                            <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                        </div>
                        <div class="lotbet-star-decoration bottom-right">
                            <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                        </div>
                        <div class="lotbet-star-decoration top-center-right">
                            <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                        </div>
                        <div class="lotbet-star-decoration bottom-left">
                            <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                        </div>
                        
                        <div class="lotbet-bonus-content-container">
                            <div class="lotbet-bonus-icon"></div>
                            
                            <div class="lotbet-bonus-text-group">
                                <div class="lotbet-bonus-description">{{$t('you-will-get','You will get')}} <strong>{{ hasFirstDepositBonus.percentage }}% {{$t('bonus','bonus')}}</strong> {{$t('on-first-deposit','on first deposit')}}.</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="lotbet-new-every-bonus-wrapper" v-if="hasEveryDepositBonus">
                    <div class="lotbet-bonus-card clickbet-every-login-bonus">
                        <div class="lotbet-star-decoration top-left">
                            <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                        </div>
                        <div class="lotbet-star-decoration bottom-right">
                            <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                        </div>
                        <div class="lotbet-star-decoration top-center-right">
                            <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                        </div>
                        <div class="lotbet-star-decoration bottom-left">
                            <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                        </div>
                        
                        <div class="lotbet-bonus-content-container">
                            <div class="lotbet-bonus-icon lotbet-bonus-icon-gift1"></div>
                            
                            <div class="lotbet-bonus-text-group">
                                <div class="lotbet-bonus-description">{{$t('you-will-get','You will get')}} <strong>{{ hasEveryDepositBonus.percentage }}% {{$t('bonus','bonus')}}</strong> {{$t('on-every-deposit','on every deposit')}}.</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="deposit top-cards_wrapper payment_option_wrapper">
                    <template v-if="parentPaymentsList">
                        <div class="deposit__title">{{$t('payment-options','Payment Options')}}</div>
                        <div class="payment-option-sec">
                            <ul class="payment-bg-sec nav nav-pills" id="pills-tab" role="tablist">
                                <li class="nav-item" role="presentation" v-for="(payment, index) in parentPaymentsList"
                                    :key="index" @click="changePaymentMethod(payment, index)">
                                    <button class="nav-link" :class="{ active: activeTab === index, 'gateway-button' : index.includes('Instant') }" id="pills-home-tab"
                                        data-bs-toggle="pill" data-bs-target="#pills-home" type="button" role="tab"
                                        aria-controls="pills-home" aria-selected="true">
                                        <div class="item-123">
                                            <div class="check">
                                                <div class="lotus-get-bonus-sec" v-if="paymentBonusRef?.[payment?.[0]?.method_id]">Get {{ paymentBonusRef?.[payment?.[0]?.method_id] }}% Bonus</div>
                                                <div class="deposit-option-list">
                                                    <div class="payments__row-item-radio">
                                                        <div class="unSelectedRadio"></div>
                                                    </div>
                                                    <div class="payment-option-logo">
                                                        <img class="payments__row-item-logo-img"
                                                            :src="methodIcons[index]"
                                                            alt="payment-image" loading="lazy" />
                                                    </div>
                                                    <span class="payments__row-item-logo-title">{{ index }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </template>
                    <div class="deposit top-cards_wrapper p-0 m-0 mt-2" v-else-if="!parentPaymentsList">
                        <div class="page-subtitle notes ps-0 text-center">
                            <p>{{$t('no-options-available','No Options Available')}}</p>
                        </div>
                    </div>
                </div>
                <div class="bank-deposit-heading paragraph-heading" v-if="currentPaymentId == 'bank'">
                            <p>{{ $t('you-can-through-bank-transfer','You can add through a bank transfer') }}</p>
                        </div>
                <div class="tab-content" id="pills-tabContent">
                    <div class="tab-pane fade show active" id="pills-home" role="tabpanel" aria-labelledby="pills-home-tab">
                        <template v-if="accountDetails && !isPaymentGateway && accountDetails.method != 'social'">
                            <BankAccountDetails :bankAccounts="paymentDetailsList" @copyText="copyText" @change-account="changeAccount" v-if="currentPaymentId == 'bank'" />
                            <UpiAccountDetails :upiAccounts="paymentDetailsList" @copyText="copyText" @change-account="changeAccount" v-else />
                            <div class="deposit total-amount mt-1" ref="utrSection">
                                <div class="deposit__title">{{$t('upload-your-payment-slip-below','Upload your payment slip below')}} *</div>
                                <div class="text-center file_upload_img_sec file-uploaded-preview-sec" v-if="previewImage">
                                    <div @click="removeImage()" class="file_close_btn">
                                        <i class="fa fa-times-circle"></i>
                                    </div>
                                    <img :src="previewImage" width="200px" />
                                    <div class="loader_timer" v-if="utrLoader">
                                        <div class="loader_deposit fileList-loader">
                                            <div class="loader">
                                                <div class="sk-fading-circle">
                                                    <div class="sk-circle1 sk-circle"></div>
                                                    <div class="sk-circle2 sk-circle"></div>
                                                    <div class="sk-circle3 sk-circle"></div>
                                                    <div class="sk-circle4 sk-circle"></div>
                                                    <div class="sk-circle5 sk-circle"></div>
                                                    <div class="sk-circle6 sk-circle"></div>
                                                    <div class="sk-circle7 sk-circle"></div>
                                                    <div class="sk-circle8 sk-circle"></div>
                                                    <div class="sk-circle9 sk-circle"></div>
                                                    <div class="sk-circle10 sk-circle"></div>
                                                    <div class="sk-circle11 sk-circle"></div>
                                                    <div class="sk-circle12 sk-circle"></div>
                                                </div>
                                            </div>
                                            <text id="myTimer" text-anchor="middle" x="100" y="110" style="font-size: 40px; fill:#343434;">
                                                {{ timerValue }}
                                            </text>
                                        </div>
                                    </div>
                                </div>
                                <div class="paymentProofFileUploadStatus" v-if="paymentProofFileUploadStatus">{{ paymentProofFileUploadStatus }}</div>   
                                <label class="sc-beySbM jncdCN imageUploader " for="file" v-if="!previewImage">
                                    <input @change="onUploadImageFunc($event)" @click="onFilePickerOpen" ref="fileRef" accept="image/*" type="file" name="file" id="file" />
                                    <i>
                                        <img src="@/assets/images/upload-photo-img.webp" alt="upload-photo-img">
                                    </i>
                                    <div class="sc-dmyDGi PLPSy">
                                        <span class="sc-hLQRIN iDaYzZ">
                                            <span>{{$t('upload','Upload')}}</span>&nbsp;{{$t('upload-or-drop-a-file-right-here','or Drop a File Right Here')}}</span>
                                    </div>
                                </label>
                                <span class="text-danger" v-if="init.transaction_image_url && !uploadedImage">{{$t('upload-your-payment-slip-below','Upload your payment slip below')}}

                                </span>
                            </div>

                            <div class="deposit total-amount total-amount">
                                <div class="deposit-utr-title">
                                    <p class="mb-1">UTR Number  <span v-if="accountDetails?.method != 'wallet'">*</span></p>
                                </div>
                                <input type="text" :maxlength="accountDetails?.method != 'wallet' ? 15 : 128" v-model="manual_utr" @input="sanitizeInput(), has_edited_utr = 1" :placeholder="$t('enter-utr-number','Enter UTR Number')"
                                    class="enter-amount-box">
                                <span class="text-danger" v-if="init.utr && !manual_utr?.length && accountDetails?.method != 'wallet'">{{$t('please-enter-utr-number','Please Enter UTR Number.')}}</span>
                                <span class="text-danger" v-else-if="init.utr && manual_utr?.length < 10 && accountDetails?.method != 'wallet'">{{$t('utr-limit-msg','UTR Number Must Be 10 to 15 Digits')}}</span>
                            </div>
                            
                            <div class="deposit deposit-amountInput total-amount depo-edit-sec" v-if="accountDetails?.method == 'wallet' && accountDetails.multiplier_value">
                                <p class="mb-1">Wallet Deposit Amount ( at x{{ accountDetails.multiplier_value }})</p>
                                <input type="number" :placeholder="`${depositAmount/accountDetails?.multiplier_value} ${accountDetails?.method_name}`" disabled class="enter-amount-box">
                            </div>

                            <div class="deposit-amountInput total-amount depo-edit-sec">
                                <p class="mb-1">{{$t('amount','Amount')}} *</p>
                                <input type="number" v-model="depositAmount" disabled
                                    class="enter-amount-box">
                                <span class="deposit-amountInput-currency" @click="$router.push('/wallet-deposit')"><span class="fa fa-edit"></span> {{$t('edit','Edit')}}</span>
                            </div>
                            
                        </template>
                        <div class="deposit total-amount mt-1" v-else-if="accountDetails && isPaymentGateway">
                            <div class="hide-input-sec payment-gateway-sec">
                                <span>
                                    {{$t('pay-using-our-payment-gateway','Pay using our payment gateway')}}
                                </span>
                                <span class="matka-but-dflex pg-btn my">
                                    <a href="Javascript:void(0);" @click="goToPaymentGatewayFunc()" class="thm-but">{{ $t('pay-now', 'Pay Now') }}</a>
                                </span>
                            </div>
                        </div>
                        <div class="deposit top-cards_wrapper p-0 m-0" v-else-if="!accountDetails && !paymentDetailsList && accountDetails?.method == 'social'">
                            <div class="page-subtitle notes ps-0">
                                <p>{{$t('no-options-contact','No Options Available. Contact admin')}}!!</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="choose-odds-sec" v-if="parentPaymentsList">
                <div class="tab-content" id="pills-tabContent">
                    <div class="tab-pane fade show active" id="pills-home" role="tabpanel" aria-labelledby="pills-home-tab">
                        <template v-if="accountDetails && !isPaymentGateway && accountDetails.method != 'social'">
                            <div class="deposit rules-button bg-transparant">
                                <div class="rulesAccept mt-0">
                                    <div class="form-check">
                                        <input class="form-check-input" v-model="termCondition" type="checkbox" id="consolidate"  />
                                        <label class="form-check-label">
                                            <span class="rulesAccept__text">{{$t('the-terms-of-payment-and-withdrawal-policy','I have read and agree with')}}&nbsp;
                                                <a href="#payment-policy" data-bs-toggle="modal" class="rulesAccept__text-underline">{{$t('reason','the terms of payment and  withdrawal policy')}}.</a>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                                <div class="proceed-btn depo_proceed_btn" ref="sbtBtn">
                                    <button type="button" @click="submitDepositAmounts()"> {{$t('proceed','Proceed')}}</button>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
    

    <div class="deposit-amount-wrapper">
        <div class="deposit-desktop d-flex">
            <div class="deposit-desktop__leftpage">
                <div class="deposit-left-side-container">
                    <div class="wallet-no-bx">
                        <div class="deposit-head">
                            <h3>{{$t('deposit','Deposit')}}</h3>
                        </div>
                    </div>
                    <div class="tab-content" id="pills-tabContent">
                        <div class="tab-pane fade show active" id="bank1-tab" role="tabpanel" aria-labelledby="pills-home-tab" v-if="accountDetails && !isPaymentGateway">
                            <BankAccountDetails :bankAccounts="paymentDetailsList" @change-account="changeAccount" @copyText="copyText" v-if="currentPaymentId == 'bank'" />
                            <UpiAccountDetails :upiAccounts="paymentDetailsList" @change-account="changeAccount" @copyText="copyText" v-else />
                        </div>
                        <div class="deposit top-cards_wrapper p-0 m-0" v-if="!accountDetails && !isPaymentGateway && paymentDetailsList && !paymentDetailsList.length">
                            <div class="page-subtitle notes ps-0">
                                <p>{{$t('no-options-contact','No Deposit Options Available. Contact admin!!')}}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="deposit-desktop__rightpage desktop-wallet-inst-wrapper">
                <div class="deposit-right-side-container deposit-amount-container vido_hide">
                    <div class="payment-option-sec" v-if="parentPaymentsList">
                        <ul class="payment-bg-sec nav nav-pills" id="pills-tab" role="tablist">
                            <li class="nav-item" role="presentation" v-for="(payment, index) in parentPaymentsList"
                                :key="index" @click="changePaymentMethod(payment, index)">
                                <button class="nav-link" :class="{ active: activeTab === index }" id="pills-home-tab"
                                    data-bs-toggle="pill" data-bs-target="#pills-home" type="button" role="tab"
                                    aria-controls="pills-home" aria-selected="true">
                                    <div class="item-123">
                                        <div class="check">
                                            <div class="lotus-get-bonus-sec" v-if="paymentBonusRef?.[payment?.[0]?.method_id]">Get {{ paymentBonusRef?.[payment?.[0]?.method_id] }}% Bonus</div>
                                            <div class="deposit-option-list">
                                                <div class="payments__row-item-radio">
                                                    <div class="unSelectedRadio"></div>
                                                </div>
                                                <div class="payment-option-logo">
                                                    <img class="payments__row-item-logo-img"
                                                        :src="methodIcons[index]"
                                                        alt="payment-image" loading="lazy" />
                                                </div>
                                                <span class="payments__row-item-logo-title">{{ index }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            </li>
                        </ul>
                    </div>
                    <div class="deposit top-cards_wrapper p-0 m-0" v-else-if="!parentPaymentsList">
                        <div class="page-subtitle notes ps-0">
                            <p>{{$t('no-options-available-amount','No Options Available. Enter different amount')}}.</p>
                        </div>
                    </div>
                    <div class="lotbet-new-every-bonus-wrapper" v-if="hasFirstDepositBonus">
                        <div class="lotbet-bonus-card clickbet-first-login-bonus">
                            <div class="lotbet-star-decoration top-left">
                                <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                            </div>
                            <div class="lotbet-star-decoration bottom-right">
                                <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                            </div>
                            <div class="lotbet-star-decoration top-center-right">
                                <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                            </div>
                            <div class="lotbet-star-decoration bottom-left">
                                <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                            </div>
                            
                            <div class="lotbet-bonus-content-container">
                                <div class="lotbet-bonus-icon"></div>
                                
                                <div class="lotbet-bonus-text-group">
                                    <div class="lotbet-bonus-description">{{$t('you-will-get','You will get')}} <strong>{{ hasFirstDepositBonus.percentage }}% {{$t('bonus','bonus')}}</strong>{{$t('on-first-deposit','on first deposit')}}.</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="lotbet-new-every-bonus-wrapper" v-if="hasEveryDepositBonus">
                        <div class="lotbet-bonus-card clickbet-every-login-bonus">
                            <div class="lotbet-star-decoration top-left">
                                <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                            </div>
                            <div class="lotbet-star-decoration bottom-right">
                                <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                            </div>
                            <div class="lotbet-star-decoration top-center-right">
                                <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                            </div>
                            <div class="lotbet-star-decoration bottom-left">
                                <svg width="24" height="24" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Group"><g id="star"><path id="Vector" d="M0.405762 6.38867C10.1454 10.6987 10.1454 10.6987 14.7051 1.00031C10.1454 10.6987 10.1454 10.6987 19.885 15.0087C10.1454 10.6987 10.1454 10.6987 5.58558 20.397C10.1454 10.6987 10.1454 10.6987 0.405762 6.38867Z" fill="#FFF"></path></g></g></svg>
                            </div>
                            
                            <div class="lotbet-bonus-content-container">
                                <div class="lotbet-bonus-icon lotbet-bonus-icon-gift1"></div>
                                
                                <div class="lotbet-bonus-text-group">
                                    <div class="lotbet-bonus-description">{{$t('you-will-get','You will get')}}<strong>{{ hasEveryDepositBonus.percentage }}% {{$t('bonus','bonus')}}</strong> {{$t('every-deposit','on every deposit.')}}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="amount-sec deposit-amount-sec">
                        <template v-if="accountDetails && !isPaymentGateway">
                            <div class="deposit total-amount" ref="utrSection">
                                <div class="deposit__title">{{$t('upload-your-payment-slip-below','Upload your payment slip below')}} </div>
                                <div class="text-center file-upload-in-desktop-view" v-if="previewImage">
                                    <figure>
                                        <button type="button" class="file-close-btn12" @click="removeImage()">
                                            <i class="fa fa-times-circle"></i>
                                        </button>
                                        <img :src="previewImage" width="200px" />
                                    </figure>
                                </div>
                                <label class="sc-beySbM jncdCN imageUploader " for="file">
                                    <input accept="image/*" @change="onUploadImageFunc($event)" type="file" name="file">
                                    <i><img src="@/assets/images/upload-photo-img.webp" alt="upload-photo-img"> </i>
                                    <div class="sc-dmyDGi PLPSy"><span class="sc-hLQRIN iDaYzZ"><span>{{$t('upload','Upload')}}</span>&nbsp;{{$t('upload-or-drop-a-file-right-here','or Drop a File Right Here')}}</span></div>
                                </label>
                            </div>
                            <div class="deposit total-amount total-amount">
                                <div class="deposit-utr-title">
                                    <p class="mb-1">{{$t('utr-number','UTR Number')}}.</p>
                                    <span v-if="accountDetails?.method != 'wallet'">*</span>
                                </div>
                                <input type="text" v-model="manual_utr" @input="sanitizeInput()" :placeholder="$t('please-enter-utr-number','Enter UTR number')" :maxlength="accountDetails?.method != 'wallet' ? 15 : 128"
                                    class="enter-amount-box">
                                    <span class="text-danger" v-if="init.utr && !manual_utr?.length && accountDetails?.method != 'wallet'">{{$t('please-enter-utr-number','Please Enter UTR Number.')}}</span>
                                    <span class="text-danger" v-else-if="init.utr && manual_utr?.length < 10 && accountDetails?.method != 'wallet'">{{$t('utr-limit-msg','UTR Number Must Be 10 to 15 Digits')}}</span>
                            </div>
                            <div class="deposit-amountInput total-amount depo-edit-sec">
                                <p class="mb-1">{{$t('amount','Amount *')}}</p>
                                <input type="number" v-model="depositAmount" disabled
                                    class="enter-amount-box">
                                <span class="deposit-amountInput-currency" @click="$router.push('/wallet-deposit')"><span class="fa fa-edit"></span> {{$t('edit','Edit')}}</span>
                            </div>
                            <div class="deposit">
                                <div class="rules-button">
                                    <div class="rulesAccept">
                                        <div class="form-check"><input type="checkbox" v-model="termCondition" value="true" id="consolidate" class="form-check-input"> 
                                            <label class="form-check-label">
                                                <span class="rulesAccept__text">
                                                    {{$t('i-have-read-and-agree-with','I have read and agree with')}}&nbsp;
                                                    <a href="#payment-policy" data-bs-toggle="modal"
                                                        class="rulesAccept__text-underline">
                                                        {{$t('the-terms-of-payment-and-withdrawal-policy','the terms of payment and withdrawal policy.')}}
                                                    </a>
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                    <div class="proceed-btn" @click="submitDepositAmounts()" ref="sbtBtn">
                                        <button type="button">
                                            {{$t('proceed','Proceed')}}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <div class="deposit total-amount mt-1" v-else-if="accountDetails && isPaymentGateway">
                            <div class="hide-input-sec payment-gateway-sec">
                                <span>
                                    {{$t('pay-using-our-payment-gateway','Pay using our payment gateway')}}
                                </span>
                                <span class="matka-but-dflex pg-btn">
                                    <a href="Javascript:void(0);" @click="goToPaymentGateway()" class="thm-but">{{ $t('pay-now', 'Pay Now') }}</a>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- <div class="wallet-inst-video-sec">
                    <WalletInstructionVideos/>
                </div> -->
            </div>
        </div>
    </div>
</template>

<style scoped>
.disabled {
    pointer-events: none;
    opacity: 0.5;
}
</style>
<style>
.payment-gateway-sec {
    display: flex;
    flex-direction: column;
    text-align: center;
}

.pg-btn {
    margin-top: 10px;
    margin-bottom: 10px;
}


.lotbet-new-every-bonus-wrapper {
    padding: 2px;
    max-width: 500px;
    margin: 0 auto;
}

.lotbet-bonus-card {
    background: linear-gradient(90deg, #134146 10.83%,#2fa0ac 130%);
    border-radius: 10px;
    padding: 7px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-family: cursive;
    position: relative;
    overflow: hidden;
}
.clickbet-first-login-bonus{
    background: linear-gradient(90deg, #134146 10.83%,#2fa0ac 130%);
}
.clickbet-every-login-bonus {
    background: linear-gradient(90deg, #000000 10.83%, #0099aa 130%);
}
.lotbet-star-decoration {
    position: absolute;
    opacity: 0.73;
}
.lotbet-star-decoration.top-left {
    top: -2px;
    left: 1px;
}

.lotbet-star-decoration.bottom-right {
    bottom: -5px; 
    right: 5px;
}

.lotbet-star-decoration.top-center-right {
    top:5%;
    right: 20%;
}

.lotbet-star-decoration.bottom-left {
    bottom: -5px;
    left: 10%;
}

.lotbet-bonus-content-container {
    display: flex;
    align-items: center;
}

.lotbet-bonus-icon {
    width: 45px;
    height: 45px;
    margin-right: 10px;
    background-image: url('@/assets/images/gift.svg');
    background-size: cover;
    flex-shrink: 0;
}

.lotbet-bonus-icon-gift1 {
    background-image: url('@/assets/images/gift1.svg');
}
.lotbet-bonus-text-group {
    display: flex;
    flex-direction: column;
    text-align: left; 
}

.lotbet-bonus-description {
    font-size: 13px;
    opacity: 1;
    line-height: normal;
}

.lotusexch-first-login-bonus {
    background: linear-gradient(90deg, #104d3d 10.83%, #1e9b7c 130%);
}
.lotusexch-every-login-bonus {
    background: linear-gradient(90deg, #4b134f 10.83%, #c94b4b 130%);
}
.guide-list li{
    list-style: auto !important;
}
</style>